import React, { useState } from 'react'

interface AnalysisItem {
  has_chatbot: boolean
  services: string[] | null
  url: string
}

interface AnalyzeResult {
  source: string
  success: boolean
  with_chatbot: AnalysisItem[]
  without_chatbot: AnalysisItem[]
}

interface FilesTableProps {
  searchQuery?: string
  analyzeResult?: AnalyzeResult | null
}

const ChatbotIcon: React.FC<{ hasChatbot: boolean }> = ({ hasChatbot }) => {
  const style = hasChatbot
    ? { backgroundColor: 'rgba(25, 135, 84, 0.1)', color: '#198754' }
    : { backgroundColor: 'rgba(220, 53, 69, 0.1)', color: '#dc3545' }

  return (
    <div
      className='rounded-3 d-flex align-items-center justify-content-center'
      style={{
        width: '40px',
        height: '40px',
        ...style
      }}
    >
      {hasChatbot ? (
        <svg width='20' height='20' fill='currentColor' viewBox='0 0 24 24'>
          <path d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z' />
        </svg>
      ) : (
        <svg width='20' height='20' fill='currentColor' viewBox='0 0 24 24'>
          <path d='M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z' />
        </svg>
      )}
    </div>
  )
}

const FilesTable: React.FC<FilesTableProps> = ({ searchQuery = '', analyzeResult = null }) => {
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(
    null
  )

  // Combine with_chatbot and without_chatbot into a single array
  const allResults: AnalysisItem[] = analyzeResult
    ? [...(analyzeResult.with_chatbot || []), ...(analyzeResult.without_chatbot || [])]
    : []

  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  // Filter results based on search query
  const filteredResults = allResults.filter((item) =>
    item.url.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Sort results if sort config is set
  const sortedResults = [...filteredResults].sort((a, b) => {
    if (!sortConfig) return 0

    let aValue: string | boolean = ''
    let bValue: string | boolean = ''

    switch (sortConfig.key) {
      case 'url':
        aValue = a.url
        bValue = b.url
        break
      case 'hasChatbot':
        aValue = a.has_chatbot
        bValue = b.has_chatbot
        break
      case 'services':
        aValue = a.services?.join(', ') || ''
        bValue = b.services?.join(', ') || ''
        break
      default:
        return 0
    }

    if (typeof aValue === 'boolean') {
      return sortConfig.direction === 'asc'
        ? (aValue ? 1 : 0) - (bValue ? 1 : 0)
        : (bValue ? 1 : 0) - (aValue ? 1 : 0)
    }

    if (sortConfig.direction === 'asc') {
      return aValue.localeCompare(bValue as string)
    } else {
      return (bValue as string).localeCompare(aValue)
    }
  })

  const SortIcon: React.FC<{ columnKey: string }> = ({ columnKey }) => (
    <svg
      width='16'
      height='16'
      className='ms-1'
      fill='none'
      stroke={sortConfig?.key === columnKey ? '#ffffff' : 'rgba(255, 255, 255, 0.6)'}
      viewBox='0 0 24 24'
      style={{
        display: 'inline-block',
        transform:
          sortConfig?.key === columnKey && sortConfig.direction === 'desc'
            ? 'rotate(180deg)'
            : 'none',
        transition: 'transform 0.2s'
      }}
    >
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth={2}
        d='M7 11l5-5m0 0l5 5m-5-5v12'
      />
    </svg>
  )

  // Empty state component
  const EmptyState = () => (
    <div className='text-center py-5'>
      <div className='mb-3'>
        <svg
          width='64'
          height='64'
          fill='none'
          stroke='#adb5bd'
          viewBox='0 0 24 24'
          className='mx-auto'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={1.5}
            d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
          />
        </svg>
      </div>
      <h6 className='text-muted fw-medium'>No Analysis Data</h6>
      <p className='text-muted small mb-0'>
        Enter URLs above and click "Update" to analyze websites for chatbot detection.
      </p>
    </div>
  )

  return (
    <div className='card border-0 shadow-sm rounded-4 overflow-hidden'>
      {/* Table Header - Results count */}
      <div className='card-header bg-white border-bottom px-4 py-3'>
        <div className='d-flex align-items-center justify-content-between'>
          <div>
            <div className='d-flex align-items-center gap-2'>
              <h5 className='mb-0 fw-semibold'>Analysis Results</h5>
              <span className='small fw-medium' style={{ color: '#6366F1' }}>
                {sortedResults.length} Total
              </span>
            </div>
            <p className='text-muted small mb-0 mt-1'>Websites analyzed for chatbot detection.</p>
          </div>
        </div>
      </div>

      {/* Table or Empty State */}
      {sortedResults.length === 0 ? (
        <EmptyState />
      ) : (
        <div className='table-responsive'>
          <table className='table table-hover mb-0 align-middle'>
            <thead style={{ backgroundColor: '#6366F1' }}>
              <tr>
                <th
                  className='px-4 py-3 border-0 text-uppercase small text-white fw-semibold'
                  style={{ letterSpacing: '0.5px', width: '60px' }}
                >
                  S.No
                </th>
                <th
                  className='px-4 py-3 border-0 text-uppercase small text-white fw-semibold'
                  style={{ cursor: 'pointer', letterSpacing: '0.5px' }}
                  onClick={() => handleSort('url')}
                >
                  Website URL
                  <SortIcon columnKey='url' />
                </th>
                <th
                  className='px-4 py-3 border-0 text-uppercase small text-white fw-semibold'
                  style={{ cursor: 'pointer', letterSpacing: '0.5px' }}
                  onClick={() => handleSort('hasChatbot')}
                >
                  Has Chatbot
                  <SortIcon columnKey='hasChatbot' />
                </th>
                <th
                  className='px-4 py-3 border-0 text-uppercase small text-white fw-semibold'
                  style={{ cursor: 'pointer', letterSpacing: '0.5px' }}
                  onClick={() => handleSort('services')}
                >
                  Services Detected
                  <SortIcon columnKey='services' />
                </th>
                <th
                  className='px-4 py-3 border-0 text-uppercase small text-white fw-semibold text-end'
                  style={{ letterSpacing: '0.5px' }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedResults.map((item, index) => (
                <tr key={`${item.url}-${index}`}>
                  <td className='px-4 py-3'>
                    <span className='fw-medium text-secondary'>{index + 1}</span>
                  </td>
                  <td className='px-4 py-3'>
                    <div className='d-flex align-items-center gap-3'>
                      <ChatbotIcon hasChatbot={item.has_chatbot} />
                      <div>
                        <a
                          href={item.url.startsWith('http') ? item.url : `https://${item.url}`}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='mb-0 small fw-medium text-decoration-none'
                          style={{ color: '#6366F1' }}
                        >
                          {item.url}
                        </a>
                      </div>
                    </div>
                  </td>
                  <td className='px-4 py-3'>
                    <span
                      className={`badge ${item.has_chatbot ? 'bg-success' : 'bg-danger'}`}
                      style={{ fontSize: '12px' }}
                    >
                      {item.has_chatbot ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className='px-4 py-3'>
                    {item.services && item.services.length > 0 ? (
                      <div className='d-flex flex-wrap gap-1'>
                        {item.services.map((service, sIndex) => (
                          <span
                            key={sIndex}
                            className='badge'
                            style={{
                              backgroundColor: 'rgba(99, 102, 241, 0.1)',
                              color: '#6366F1',
                              fontSize: '11px'
                            }}
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className='text-muted small'>—</span>
                    )}
                  </td>
                  <td className='px-4 py-3'>
                    <div className='d-flex align-items-center justify-content-end gap-2'>
                      <a
                        href={item.url.startsWith('http') ? item.url : `https://${item.url}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='btn btn-link small fw-medium p-0 text-decoration-none'
                        style={{ color: '#6366F1' }}
                      >
                        Visit
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default FilesTable
